import React from 'react';

const styles = {
    container: { marginTop: '20px' },
    cardGrid: {
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px', marginBottom: '30px',
    },
    card: {
        backgroundColor: '#f5f3fa', borderRadius: '10px', padding: '20px',
        textAlign: 'center', border: '1px solid #e0d8f0',
    },
    cardLabel: { fontSize: '12px', color: '#666', textTransform: 'uppercase', marginBottom: '6px' },
    cardValue: { fontSize: '28px', fontWeight: 'bold', color: '#4b2e83' },
    cardSub: { fontSize: '12px', color: '#999', marginTop: '4px' },
    sectionTitle: {
        fontSize: '16px', fontWeight: 'bold', color: '#4b2e83',
        marginBottom: '10px', marginTop: '20px',
    },
    table: {
        width: '100%', borderCollapse: 'collapse', fontSize: '14px', marginBottom: '20px',
    },
    th: {
        backgroundColor: '#4b2e83', color: '#fff', padding: '10px 8px',
        textAlign: 'right', borderBottom: '2px solid #ddd',
    },
    thLeft: {
        backgroundColor: '#4b2e83', color: '#fff', padding: '10px 8px',
        textAlign: 'left', borderBottom: '2px solid #ddd',
    },
    td: {
        padding: '8px', textAlign: 'right', borderBottom: '1px solid #eee',
    },
    tdLeft: {
        padding: '8px', textAlign: 'left', borderBottom: '1px solid #eee', fontWeight: 'bold',
    },
    medalBadge: (rank) => ({
        display: 'inline-block', width: '22px', height: '22px', borderRadius: '50%',
        textAlign: 'center', lineHeight: '22px', fontSize: '12px', fontWeight: 'bold',
        marginRight: '8px', color: '#fff',
        backgroundColor: rank === 1 ? '#FFD700' : rank === 2 ? '#C0C0C0' : '#CD7F32',
    }),
};

const fmt = (val) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ExecutiveSummary = ({ scores, teams, rounds: roundsProp }) => {
    const teamNames = teams.map(t => t.teamName).sort();
    const rounds = roundsProp || [1, 2, 3];

    // Aggregate data per team across all rounds
    const teamAgg = {};
    teamNames.forEach(name => {
        teamAgg[name] = { totalProfit: 0, totalRevenue: 0, totalUnits: 0, roundsPlayed: 0 };
        rounds.forEach(r => {
            const rs = scores[`round${r}`];
            if (rs && rs[name]) {
                teamAgg[name].totalProfit += rs[name].totalProfit || 0;
                teamAgg[name].totalRevenue += rs[name].totalRevenue || 0;
                teamAgg[name].totalUnits += rs[name].totalUnits || 0;
                teamAgg[name].roundsPlayed++;
            }
        });
    });

    // Sort teams by total profit for leaderboard
    const profitRanked = [...teamNames].sort((a, b) => teamAgg[b].totalProfit - teamAgg[a].totalProfit);
    const revenueRanked = [...teamNames].sort((a, b) => teamAgg[b].totalRevenue - teamAgg[a].totalRevenue);
    const unitsRanked = [...teamNames].sort((a, b) => teamAgg[b].totalUnits - teamAgg[a].totalUnits);

    // Summary stats
    const totalMarketRevenue = teamNames.reduce((s, n) => s + teamAgg[n].totalRevenue, 0);
    const totalMarketUnits = teamNames.reduce((s, n) => s + teamAgg[n].totalUnits, 0);
    const totalMarketProfit = teamNames.reduce((s, n) => s + teamAgg[n].totalProfit, 0);
    const activeRounds = rounds.filter(r => scores[`round${r}`] !== null).length;

    return (
        <div style={styles.container}>
            {/* Top-level KPIs */}
            <div style={styles.cardGrid}>
                <div style={styles.card}>
                    <div style={styles.cardLabel}>Total Market Revenue</div>
                    <div style={styles.cardValue}>{fmt(totalMarketRevenue)}</div>
                    <div style={styles.cardSub}>{activeRounds} round{activeRounds !== 1 ? 's' : ''} completed</div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardLabel}>Total Units Sold</div>
                    <div style={styles.cardValue}>{totalMarketUnits.toLocaleString()}</div>
                    <div style={styles.cardSub}>across {teamNames.length} teams</div>
                </div>
                <div style={styles.card}>
                    <div style={styles.cardLabel}>Total Market Profit</div>
                    <div style={styles.cardValue}>{fmt(totalMarketProfit)}</div>
                    <div style={styles.cardSub}>avg {fmt(teamNames.length > 0 ? totalMarketProfit / teamNames.length : 0)}/team</div>
                </div>
            </div>

            {/* Profit Leaderboard */}
            <div style={styles.sectionTitle}>Profit Leaderboard</div>
            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={{ ...styles.thLeft, width: '40px' }}>#</th>
                        <th style={styles.thLeft}>Team</th>
                        <th style={styles.th}>Total Profit</th>
                        <th style={styles.th}>Total Revenue</th>
                        <th style={styles.th}>Total Units</th>
                        <th style={styles.th}>Profit Margin</th>
                    </tr>
                </thead>
                <tbody>
                    {profitRanked.map((name, i) => {
                        const agg = teamAgg[name];
                        const margin = agg.totalRevenue > 0
                            ? ((agg.totalProfit / agg.totalRevenue) * 100).toFixed(1)
                            : '0.0';
                        return (
                            <tr key={name} style={i < 3 ? { backgroundColor: '#fafafa' } : {}}>
                                <td style={{ ...styles.td, textAlign: 'center' }}>
                                    {i < 3 ? <span style={styles.medalBadge(i + 1)}>{i + 1}</span> : i + 1}
                                </td>
                                <td style={styles.tdLeft}>{name}</td>
                                <td style={{ ...styles.td, fontWeight: i < 3 ? 'bold' : 'normal' }}>{fmt(agg.totalProfit)}</td>
                                <td style={styles.td}>{fmt(agg.totalRevenue)}</td>
                                <td style={styles.td}>{agg.totalUnits.toLocaleString()}</td>
                                <td style={styles.td}>{margin}%</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Round-by-Round Summary */}
            <div style={styles.sectionTitle}>Round-by-Round Market Summary</div>
            <table style={styles.table}>
                <thead>
                    <tr>
                        <th style={styles.thLeft}>Metric</th>
                        {rounds.map(r => (
                            <th key={r} style={styles.th}>Round {r}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={styles.tdLeft}>Market Revenue</td>
                        {rounds.map(r => {
                            const rs = scores[`round${r}`];
                            if (!rs) return <td key={r} style={styles.td}>-</td>;
                            const total = teamNames.reduce((s, n) => s + (rs[n]?.totalRevenue || 0), 0);
                            return <td key={r} style={styles.td}>{fmt(total)}</td>;
                        })}
                    </tr>
                    <tr>
                        <td style={styles.tdLeft}>Market Units</td>
                        {rounds.map(r => {
                            const rs = scores[`round${r}`];
                            if (!rs) return <td key={r} style={styles.td}>-</td>;
                            const total = teamNames.reduce((s, n) => s + (rs[n]?.totalUnits || 0), 0);
                            return <td key={r} style={styles.td}>{total.toLocaleString()}</td>;
                        })}
                    </tr>
                    <tr>
                        <td style={styles.tdLeft}>Market Profit</td>
                        {rounds.map(r => {
                            const rs = scores[`round${r}`];
                            if (!rs) return <td key={r} style={styles.td}>-</td>;
                            const total = teamNames.reduce((s, n) => s + (rs[n]?.totalProfit || 0), 0);
                            return <td key={r} style={styles.td}>{fmt(total)}</td>;
                        })}
                    </tr>
                    <tr>
                        <td style={styles.tdLeft}>Leader (Profit)</td>
                        {rounds.map(r => {
                            const rs = scores[`round${r}`];
                            if (!rs) return <td key={r} style={styles.td}>-</td>;
                            let best = '', bestVal = -Infinity;
                            teamNames.forEach(n => {
                                const p = rs[n]?.totalProfit || 0;
                                if (p > bestVal) { bestVal = p; best = n; }
                            });
                            return <td key={r} style={{ ...styles.td, fontWeight: 'bold', color: '#4b2e83' }}>{best}</td>;
                        })}
                    </tr>
                </tbody>
            </table>

            {/* Quick category winners */}
            <div style={styles.sectionTitle}>Category Leaders (Cumulative)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginBottom: '20px' }}>
                {[
                    { label: 'Most Profitable', team: profitRanked[0], value: fmt(teamAgg[profitRanked[0]]?.totalProfit || 0) },
                    { label: 'Highest Revenue', team: revenueRanked[0], value: fmt(teamAgg[revenueRanked[0]]?.totalRevenue || 0) },
                    { label: 'Most Units Sold', team: unitsRanked[0], value: (teamAgg[unitsRanked[0]]?.totalUnits || 0).toLocaleString() },
                ].map(cat => (
                    <div key={cat.label} style={{
                        padding: '16px', backgroundColor: '#fff', border: '2px solid #4b2e83',
                        borderRadius: '8px', textAlign: 'center',
                    }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>{cat.label}</div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#4b2e83' }}>{cat.team}</div>
                        <div style={{ fontSize: '14px', color: '#333', marginTop: '4px' }}>{cat.value}</div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ExecutiveSummary;
